import type { ComputedRef, Ref } from 'vue'
import { computed, watch } from 'vue'
import { useCookie, useState } from '#imports'

// Where the „7 realizací a 12 návrhů" counts line sits relative to a CMS-block CTA button:
// as a second line INSIDE the button (default) or as a caption BELOW it. Switched from the
// admin side panel (AdminFontSizeSlider); persisted in a cookie so SSR renders the chosen variant
// (no flash) and the choice survives reloads. Blocks read it through this same composable, so the
// file stays portable to the admin repo's copy of shared-project-components (there the cookie is
// simply unset → default).

export type CtaCountsPlacement = 'inside' | 'below'

export const CTA_COUNTS_PLACEMENT_DEFAULT: CtaCountsPlacement = 'inside'
export const CTA_COUNTS_PLACEMENTS: CtaCountsPlacement[] = ['inside', 'below']

const cookieKey = 'cta-counts-placement'

const normalize = (value: unknown): CtaCountsPlacement =>
  value === 'below' ? 'below' : 'inside'

export interface UseCtaCountsPlacementReturn {
  placement: Ref<CtaCountsPlacement>
  /** Counts render as a second line inside the button. */
  isInside: ComputedRef<boolean>
  /** Counts render as a caption under the button. */
  isBelow: ComputedRef<boolean>
  setPlacement: (value: CtaCountsPlacement) => void
}

export const useCtaCountsPlacement = (): UseCtaCountsPlacementReturn => {
  const cookie = useCookie<CtaCountsPlacement>(cookieKey, {
    default: () => CTA_COUNTS_PLACEMENT_DEFAULT,
    sameSite: 'lax',
    maxAge: 60 * 60 * 24 * 365,
  })

  // useCookie reads the request cookie during SSR; useState shares the value app-wide.
  const placement = useState<CtaCountsPlacement>(cookieKey, () => normalize(cookie.value))

  watch(placement, (value) => {
    cookie.value = value
  })

  const setPlacement = (value: CtaCountsPlacement) => {
    placement.value = normalize(value)
  }

  return {
    placement,
    isInside: computed(() => placement.value !== 'below'),
    isBelow: computed(() => placement.value === 'below'),
    setPlacement,
  }
}
